import React, { useRef, useState, useEffect } from 'react';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import listAnalytics from './listAnalytics';
import TemplateParser from '../../components/Templates/TemplateParser';
import { unique, difference } from '../../utils/array';
import { translate, removeLanguagePrefix } from '../../utils/locale';
import { hasTemplateInObj, extractTemplateFromObj } from '../../utils/object';
import s from './Pagination.scss';

let itemsSeen = [];
let itemsSent = [];
let indexes = {};

const Pagination = ({
	ListComponent,
	contentSections = {},
	variantClasses = [],
	setup,
	list,
	cache,
	currentPath,
	gridAmount = 4,
	showingGhost = false,
	currentHash = '',
	canonical = false,
	magazine = false,
	closeSearch = () => {},
}) => {
	const paginationContainer = useRef(null);
	const [introExpanded, toggleIntro] = useState(false);
	const pageBase = unique(list || cache);
	const renderObjects = [];
	const itemsToRender = [];
	let objectSpaces = 0;
	let itemRowLength = 1;

	useEffect(() => {
		itemsSeen = [];
		itemsSent = [];
		indexes = {};
	}, [currentPath]);

	useEffect(() => {
		if (paginationContainer.current !== null) {
			const options = {
				root: null,
				rootMargin: '0px',
				threshold: [0.5],
			};

			const handleIntersect = (elements) => {
				elements.forEach((el) => {
					if (el.isIntersecting) {
						const id = el.target.dataset.productId;
						indexes[id] = el.target.dataset.productIndex;
						if (itemsSeen.indexOf(id) === -1) itemsSeen.push(id);
					}
				});
				const itemsToSend = difference(itemsSeen, itemsSent);
				if (itemsToSend.length > 0) {
					listAnalytics(itemsToSend.map((i) => cache[i]), itemsToSend.map((i) => indexes[i]), setup, removeLanguagePrefix(currentPath) || 'productPicker');
					itemsSent = [...itemsSent, ...itemsToSend];
				}
			};

			const observer = new window.IntersectionObserver(handleIntersect, options);
			paginationContainer.current.querySelectorAll('.u_productItem').forEach((e) => observer.observe(e));
		}
	}, [list]);

	let paginationClasses = [s.pagination, 'u_plp', s[`pagination__${gridAmount}`]];
	let inlineContentClasses = [s.pagination_inlineContent];
	if (pageBase.length < 5) paginationClasses.push(s.pagination__centered);
	if (variantClasses) {
		paginationClasses = [...paginationClasses, ...(Array.isArray(variantClasses) ? variantClasses.filter((c) => !!c && c.indexOf('pagination__') !== -1).map((c) => s[c]) : [s[variantClasses.toString()]])];
		inlineContentClasses = [...inlineContentClasses, ...(Array.isArray(variantClasses) ? variantClasses.filter((c) => !!c && c.indexOf('pagination_inlineContent__') !== -1).map((c) => s[c]) : [s[variantClasses.toString()]])];
	}

	if (typeof window === 'undefined') {
		if (setup.breakPoint === 'desktop' && parseInt(gridAmount, 10) === 4) {
			itemRowLength = 4;
		} else if ((setup.breakPoint === 'desktop' && parseInt(gridAmount, 10) === 3) || (setup.breakPoint === 'tablet' && parseInt(gridAmount, 10) === 4)) {
			itemRowLength = 3;
		} else if ((setup.breakPoint === 'tablet' && parseInt(gridAmount, 10) === 3) || (setup.breakPoint === 'mobile' && parseInt(gridAmount, 10) === 4)) {
			itemRowLength = 2;
		}
	} else if (window.innerWidth > 1100 && parseInt(gridAmount, 10) === 4) {
		itemRowLength = 4;
	} else if ((window.innerWidth > 1100 && parseInt(gridAmount, 10) === 3) || (window.innerWidth > 900 && window.innerWidth <= 1100 && parseInt(gridAmount, 10) === 4)) {
		itemRowLength = 3;
	} else if ((window.innerWidth > 900 && window.innerWidth <= 1100 && parseInt(gridAmount, 10) === 3) || (window.innerWidth <= 900 && parseInt(gridAmount, 10) === 4)) {
		itemRowLength = 2;
	}
	// console.log('DEBUG: itemRowLength', itemRowLength);

	const toggleIntroHandler = () => {
		toggleIntro((prevState) => !prevState);
	};

	const renderContent = (section, itemCount) => {
		const { display, width } = section.partsMeta;
		if (display === 'inline-block') {
			let hasReadMore = itemCount === 0;
			if (hasReadMore && !hasTemplateInObj(section.sections, 'coreParagraph')) hasReadMore = false;
			if (hasReadMore) {
				const { filteredSections, extractedSection } = extractTemplateFromObj(section.sections, 'coreButtons');

				return (
					<div key={`content_${section.meta.id}`} className={[...inlineContentClasses, s[`pagination_inlineContent__${width}`], (introExpanded ? s.pagination_inlineContent__expanded : null), (itemCount === 0 ? s.pagination_inlineContent__intro : null), (!hasReadMore ? s.pagination_inlineContent__noReadMore : s.pagination_inlineContent__readMore)].join(' ')}>
						<div className={s.pagination_inlineContent_inner}>
							<TemplateParser sections={filteredSections} setup={setup} inline skipfirst />
							{ hasReadMore && <button className={s.pagination_inlineContent_readMore} type="button" onClick={toggleIntroHandler}>{ translate(setup, (introExpanded ? 'plpReadLess' : 'plpReadMore')) }</button> }
						</div>
						{ extractedSection.length > 0 && <TemplateParser sections={extractedSection} setup={setup} inline skipfirst /> }
					</div>
				);
			}
			return (
				<div key={`content_${section.meta.id}`} className={[...inlineContentClasses, s[`pagination_inlineContent__${width}`], (introExpanded ? s.pagination_inlineContent__expanded : null), (itemCount === 0 ? s.pagination_inlineContent__intro : null), (!hasReadMore ? s.pagination_inlineContent__noReadMore : s.pagination_inlineContent__readMore)].join(' ')}>
					<TemplateParser sections={section.sections} setup={setup} inline skipfirst />
				</div>
			);
		}
		return <TemplateParser key={`content_${renderObjects.length}`} sections={contentSections[renderObjects.length].sections} setup={setup} inline skipfirst />;
	};

	// console.log('DEBUG: pageBase', pageBase);

	for (itemsToRender.length; itemsToRender.length < pageBase.length;) {
		let item = pageBase[itemsToRender.length];
		let id;
		if (list) {
			id = item;
			item = cache[item];
		}

		if (contentSections && contentSections[renderObjects.length]) {
			const { display, width } = contentSections[renderObjects.length].partsMeta;
			let nextWidth = 1;
			if (renderObjects.length === 0 && setup.breakPoint === 'mobile') {
				nextWidth = itemRowLength;
			} else if (display === 'inline-block') {
				nextWidth = width.split('').map((j) => parseInt(j, 10)).reduce((acc, j, k) => {
					// Handle content that's bigger than total product width
					if (k === 0 && j > itemRowLength) return acc * itemRowLength;
					return acc * j;
				}, 1);
			} else {
				nextWidth = itemRowLength;
			}

			let remainingRow = (objectSpaces + nextWidth) % itemRowLength;

			// beforeContent / afterContent checks if width makes content span multiple rows.
			/*
			console.table({
				beforeContent: Math.floor(objectSpaces / itemRowLength),
				afterContent: Math.floor((objectSpaces + nextWidth) / itemRowLength),
				remainingRow,
				objectSpaces,
				nextWidth,
				itemRowLength,
				gridAmount,
			});
			*/

			if (Math.floor(objectSpaces / itemRowLength) !== Math.floor((objectSpaces + nextWidth) / itemRowLength) && remainingRow > 0) {
				for (let j = 0; j < remainingRow; j += 1) {
					objectSpaces += 1;
					item = pageBase[itemsToRender.length];
					if (list) {
						id = item;
						item = cache[item];
					}
					renderObjects.push(<ListComponent imageSize={Math.ceil(12 / parseInt(gridAmount, 10))} variantClasses={variantClasses} key={item.uri} closeSearch={closeSearch} id={id} content={item} canonical={magazine ? translate(item.uri, setup.language).text : canonical} setup={setup} currentPath={currentPath} currentHash={currentHash} currentView={currentPath || 'productPicker'} index={itemsToRender.length} showingGhost={showingGhost} />);
					itemsToRender.push(item);
				}
			} else {
				remainingRow = 0;
			}
			renderObjects.push(renderContent(contentSections[renderObjects.length - remainingRow], renderObjects.length));
			objectSpaces += nextWidth;
		} else if (item) {
			objectSpaces += 1;
			renderObjects.push(<ListComponent imageSize={Math.ceil(12 / parseInt(gridAmount, 10))} variantClasses={variantClasses} key={item.uri} closeSearch={closeSearch} id={id} content={item} canonical={magazine ? translate(item.uri, setup.language).text : canonical} setup={setup} currentPath={currentPath} currentHash={currentHash} currentView={currentPath || 'productPicker'} index={itemsToRender.length} showingGhost={showingGhost} />);
			itemsToRender.push(item);
			// console.log('DEBUG: item no content ', item?.uri, item, itemsToRender.length);
		}
	}

	return (
		<div className={paginationClasses.join(' ')} ref={paginationContainer}>
			{ renderObjects }
			{
				renderObjects.length < contentSections.length && contentSections.slice(renderObjects.length).filter((section) => !!section).map((section, i) => renderContent(section, i))
			}
		</div>
	);
};

const mapState = ({ environment, location }, { showAll = false, paging = null, currentHash = '', canonical = false, magazine = false, ...props }) => ({
	pagination: environment.pagination,
	setup: environment.setup,
	location,
	showAll,
	paging,
	currentHash,
	canonical,
	magazine,
	...props,
});

export default hot(module)(connect(mapState)(Pagination));
